import { fillCanvasWithSnapshot, makeSnapshotCanvas } from "./snapshotBridge";

/**
 * Replace CPB's 2D Konva-stage snapshot with our 3D scene snapshot.
 *
 * Background. CPB takes a preview image of the configured product right at
 * "Add to cart" time, uploads the PNG to its own backend
 * (`app.thecustomproductbuilder.com/private-image/…`), and writes the
 * returned URL into the Shopify line item as the `_image` property. That
 * property is what shows up in /cart.js, customer-facing order pages,
 * admin order detail, and packing slips. The image itself comes from
 * Konva's `Stage.toCanvas()` — Konva renders all its layers into a fresh
 * `<canvas>` and CPB calls `.toBlob()` / `.toDataURL()` on the result.
 *
 * We hide the Konva preview visually (CSS) and silence its draw loop
 * (`silenceCpbKonvaStages()` in main.tsx), but the stage itself still
 * exists, sized to `.cpb-preview-container`. So when CPB asks it for a
 * snapshot, the Konva stage hands back blank/stale pixels.
 *
 * This module monkey-patches `Konva.Stage.prototype.toCanvas` so that
 * stages anchored inside `.cpb-preview-container` produce a fresh canvas
 * filled with our R3F scene render instead. Because Stage.toDataURL,
 * Stage.toBlob, and Stage.toImage all internally route through
 * Stage.toCanvas, hooking that one method covers the entire snapshot API.
 *
 * The patch is:
 *  - **Surgical**: only stages whose container is inside the CPB mount
 *    target are hijacked. Any other Konva stage on the page (theoretical,
 *    but possible from a third-party Shopify app) keeps working normally.
 *  - **Idempotent**: a sentinel flag on the prototype prevents double
 *    patching across HMR / re-mount.
 *  - **Resilient**: respects the caller's requested width/height/pixelRatio
 *    so CPB's snapshot upload sees the resolution it asked for.
 *
 * As a belt-and-suspenders fallback, we also patch
 * `HTMLCanvasElement.prototype.toDataURL` / `.toBlob` to reroute calls
 * targeting any DOM-attached canvas inside `.cpb-preview-container`. That
 * covers the unlikely case where CPB ever bypasses Konva's Stage API and
 * grabs a layer canvas directly.
 */

type KonvaStageProto = {
  toCanvas?: (config?: KonvaToCanvasConfig) => HTMLCanvasElement;
  toDataURL?: (config?: KonvaToCanvasConfig) => string;
  width?: () => number;
  height?: () => number;
  container?: () => Element | undefined;
  __maytrixSnapshotHijacked?: boolean;
};

type KonvaToCanvasConfig = {
  width?: number;
  height?: number;
  pixelRatio?: number;
  mimeType?: string;
  quality?: number;
  x?: number;
  y?: number;
  callback?: (canvas: HTMLCanvasElement) => void;
};

type KonvaGlobal = { Stage?: { prototype?: KonvaStageProto } };

const CPB_MOUNT = ".cpb-preview-container";
const LOG_STYLE = "color:#4b9;font-weight:bold;";

function log(label: string, ...args: unknown[]) {
  console.log(`%c[viewer] ${label}`, LOG_STYLE, ...args);
}

/**
 * True when `el` is anchored inside `.cpb-preview-container` AND attached
 * to the document. Konva keeps stage containers as live DOM nodes, so
 * `closest()` works; for transient/offscreen canvases passed through the
 * HTMLCanvasElement fallback we deliberately return false (we can't
 * disambiguate them from canvases used by, say, an unrelated 2D plugin).
 */
function isInsideCpbMount(el: Element | undefined | null): boolean {
  if (!el || !el.isConnected) return false;
  return el.closest(CPB_MOUNT) !== null;
}

/**
 * Monkey-patch `Konva.Stage.prototype.toCanvas`. Returns true once the
 * patch is applied (or detected as already applied). Returns false if
 * `window.Konva` isn't on the page yet — the caller polls in that case.
 */
function tryPatchKonva(): boolean {
  const proto = (window as unknown as { Konva?: KonvaGlobal }).Konva?.Stage?.prototype;
  if (!proto) return false;
  if (proto.__maytrixSnapshotHijacked) return true;

  const origToCanvas = proto.toCanvas;
  if (typeof origToCanvas !== "function") {
    // Konva loaded but Stage prototype shape is unexpected — probably a
    // version we don't know about. Leave it alone rather than break.
    log("Konva.Stage.toCanvas missing or non-function, skipping snapshot hijack");
    proto.__maytrixSnapshotHijacked = true;
    return true;
  }

  proto.toCanvas = function (this: KonvaStageProto, config?: KonvaToCanvasConfig) {
    const containerEl = this.container?.();
    if (!isInsideCpbMount(containerEl)) {
      return origToCanvas.call(this, config);
    }

    // Honour the caller's requested geometry. Konva defaults width/height
    // to the stage's own dimensions and pixelRatio to 1, but CPB / the
    // theme could pass any combination — the resulting PNG is what gets
    // uploaded so we want it crisp at whatever resolution they ask for.
    const baseW = config?.width ?? this.width?.() ?? 0;
    const baseH = config?.height ?? this.height?.() ?? 0;
    const pixelRatio = config?.pixelRatio ?? 1;
    const w = Math.max(1, Math.round(baseW * pixelRatio));
    const h = Math.max(1, Math.round(baseH * pixelRatio));

    return makeSnapshotCanvas(w, h);
  };

  // Some Konva versions implement `toDataURL` independently rather than
  // routing through `toCanvas` — patch it explicitly so we cover both.
  const origToDataURL = proto.toDataURL;
  if (typeof origToDataURL === "function") {
    proto.toDataURL = function (this: KonvaStageProto, config?: KonvaToCanvasConfig) {
      const containerEl = this.container?.();
      if (!isInsideCpbMount(containerEl)) {
        return origToDataURL.call(this, config);
      }
      const baseW = config?.width ?? this.width?.() ?? 0;
      const baseH = config?.height ?? this.height?.() ?? 0;
      const pixelRatio = config?.pixelRatio ?? 1;
      const w = Math.max(1, Math.round(baseW * pixelRatio));
      const h = Math.max(1, Math.round(baseH * pixelRatio));
      const canvas = makeSnapshotCanvas(w, h);
      return canvas.toDataURL(config?.mimeType ?? "image/png", config?.quality);
    };
  }

  proto.__maytrixSnapshotHijacked = true;
  log(`Konva.Stage.toCanvas/toDataURL hijacked for stages inside ${CPB_MOUNT}`);
  return true;
}

/**
 * Belt-and-suspenders patch of the raw Canvas 2D API. Routes any
 * `toDataURL` / `toBlob` call on a canvas that's currently in the DOM
 * inside `.cpb-preview-container` through our 3D snapshot first.
 *
 * Why this is separate from the Konva patch: if CPB ever switched from
 * `stage.toDataURL()` to grabbing a layer canvas directly, the Konva
 * hook wouldn't fire — but the canvas is still a Konva-managed `<canvas>`
 * sitting inside `.konvajs-content`, so this catches it.
 *
 * False-positive risk: any unrelated canvas that happens to be inside
 * `.cpb-preview-container` and calls `toDataURL` would be hijacked too.
 * In practice the only canvases there are Konva's (stage) and ours
 * (R3F, but that's `#viewer-3d-root canvas`, *also* a descendant of the
 * CPB mount). To avoid clobbering our own R3F canvas if it ever calls
 * toDataURL, we explicitly skip canvases inside `#viewer-3d-root`.
 */
function patchCanvasMethods(): void {
  const HCEProto = HTMLCanvasElement.prototype as HTMLCanvasElement & {
    __maytrixSnapshotHijacked?: boolean;
  };
  if (HCEProto.__maytrixSnapshotHijacked) return;

  const origToDataURL = HCEProto.toDataURL;
  const origToBlob = HCEProto.toBlob;

  function shouldHijack(canvas: HTMLCanvasElement): boolean {
    if (!canvas.isConnected) return false;
    if (canvas.closest("#viewer-3d-root")) return false;
    return canvas.closest(CPB_MOUNT) !== null;
  }

  HCEProto.toDataURL = function (this: HTMLCanvasElement, ...args: unknown[]) {
    if (!shouldHijack(this)) {
      return origToDataURL.apply(
        this,
        args as Parameters<HTMLCanvasElement["toDataURL"]>,
      );
    }
    const proxy = makeSnapshotCanvas(this.width, this.height);
    return origToDataURL.apply(
      proxy,
      args as Parameters<HTMLCanvasElement["toDataURL"]>,
    );
  };

  HCEProto.toBlob = function (
    this: HTMLCanvasElement,
    callback: BlobCallback,
    type?: string,
    quality?: number,
  ) {
    if (!shouldHijack(this)) {
      return origToBlob.call(this, callback, type, quality);
    }
    const proxy = makeSnapshotCanvas(this.width, this.height);
    // Re-paint into the original to keep any consumer that reads back via
    // `getImageData()` happy. Costs a single CPU drawImage per snapshot —
    // negligible compared to the WebGL render that produced the pixels.
    const ctx = this.getContext("2d");
    if (ctx) {
      ctx.clearRect(0, 0, this.width, this.height);
      fillCanvasWithSnapshot(this);
    }
    return origToBlob.call(proxy, callback, type, quality);
  };

  HCEProto.__maytrixSnapshotHijacked = true;
  log(
    `HTMLCanvasElement.toDataURL/toBlob fallback hijack installed for canvases inside ${CPB_MOUNT}`,
  );
}

/**
 * Public entrypoint. Call once from main.tsx, after `mountInto`. Polls for
 * `window.Konva` for up to 10s (CPB loads its bundle async, same as
 * `silenceCpbKonvaStages`) and patches `HTMLCanvasElement` immediately.
 */
export function installCpbSnapshotHijack(): void {
  patchCanvasMethods();

  if (tryPatchKonva()) return;

  log("Konva not on window yet — polling for it (snapshot hijack)");
  const iv = window.setInterval(() => {
    if (tryPatchKonva()) window.clearInterval(iv);
  }, 100);
  window.setTimeout(() => window.clearInterval(iv), 10_000);
}
